// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, mergeMap, withLatestFrom } from "rxjs/operators"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import { AttachTagRequestSchema, AttachTagResponse } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import * as Actions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import { showSnackBar } from "../../snackbar/actions"
import type { RootState } from "../../../app/store"
import * as TagsSlice from "../../tags/slice"
import { Code, executorMutation, Init, isConnectError, timelineService } from "../../../app/rpc"

export type AttachTagRpc = (request: Init<typeof AttachTagRequestSchema>) => Observable<AttachTagResponse>

export const attachTagFulfilled = (
  response: AttachTagResponse,
  tagCategoryId: string | undefined,
  isTimeGroupTagReplacement = false,
): Observable<Action> => {
  const actions: Action[] = []
  const timeRowsHasCategory = tagCategoryId ? response.timeRows
    .filter(row => row.tagCategories.some(category => category.id === tagCategoryId))
    .map(row => row.id) : []
  const timeGroupHasCategory = tagCategoryId ? response.timeGroups
    .filter(timeGroup => timeGroup.tagCategories.some(category => category.id === tagCategoryId))
    .map(timeGroup => timeGroup.id)[0] : undefined
  if (response.timeRows.length) {
    actions.push(TimelineSlice.updateTimeRowsInCache(response.timeRows))
  }
  if (response.timeGroups.length) {
    actions.push(TimelineSlice.updateTimeGroupsInCache(response.timeGroups))
  }
  if (timeRowsHasCategory.length > 0 && tagCategoryId) {
    actions.push(TimelineSlice.detachTagCategory({
      timeRowIds: timeRowsHasCategory,
      timeGroupId: "",
      tagCategoryId,
    }))
  }
  if (timeGroupHasCategory && tagCategoryId) {
    actions.push(TimelineSlice.detachTagCategory({
      timeRowIds: undefined,
      timeGroupId: timeGroupHasCategory,
      tagCategoryId,
    }))
  }
  // Generate timesheet field configs after all state updates
  if (response.timeGroups.length && !isTimeGroupTagReplacement) {
    actions.push(Actions.generateTimesheetFieldConfigs(response.timeGroups))
  }

  return concat(actions)
}

export const attachTag = (attachTagRpc: AttachTagRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.attachTag.match),
      withLatestFrom(state$),
      mergeMap(([{ payload: { attachTagRequest, tag }}, state]) => (
        attachTagRpc(attachTagRequest)
          .pipe(
            mergeMap(response => attachTagFulfilled(
              response,
              TagsSlice.selectTagsCache(state)[tag.id ?? ""]?.tagCategoryId,
            )),
            catchError(handleError),
          )
      )),
    )

const handleError = (e: unknown) => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      return of(showSnackBar({
        message: t({
          id: "msg_add_tag_failed_not_found",
          message: "Failed to add tag. Could not find the target time row or timesheet."
        }),
        messageType: "ERROR",
      }))
    }
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({
          id: "msg_add_tag_failed_permission_denied",
          message: "Failed to add tag. You do not have permission to add a tag to the time row or timesheet."
        }),
        messageType: "ERROR",
      }))
    }
    if (code === Code.FailedPrecondition) {
      return concat(
        of(showSnackBar({
          message: t({
            id: "msg_add_tag_failed_precondition",
            message: "Failed to add tag. Time row may be out of sync."
          }),
          messageType: "ERROR"
        })),
        of(Actions.onTimelineOutOfSync())
      )
    }
  }

  return of(showSnackBar({
    message: t({
      id: "msg_add_tag_failed",
      message: "Failed to add tag. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
    }),
    messageType: "ERROR",
  }))
}

export const attachTagRpc = (request: Init<typeof AttachTagRequestSchema>): Observable<AttachTagResponse> =>
  executorMutation(() => timelineService.attachTag(create(AttachTagRequestSchema, request)))

export default attachTag(attachTagRpc)
